import React, { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import AnimatedNumber from "@/components/AnimatedNumber";
import { getSiteConfig } from "@/siteConfig";

const steps = [
    { label: "Search", caption: "Location + business types" },
    { label: "Discover", caption: "Public emails + contact pages" },
    { label: "Score", caption: "Trust + dedupe" },
    { label: "Send", caption: "Your own SMTP" },
    { label: "Track", caption: "Timeline + suppression" },
];

export default function Hero() {
    const [active, setActive] = useState(0);
    const { version } = getSiteConfig();
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative border-b border-border overflow-hidden"
        >
            <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-16 md:pb-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                {/* Left: copy */}
                <div className="lg:col-span-6 fade-up">
                    <div className="flex items-center gap-3 mb-8">
                        <span className="overline">001 / Lead Discovery</span>
                        <span className="h-px w-12 bg-border" />
                        <span className="overline">Windows · v{version}</span>
                    </div>

                    <h1 className="font-display text-[44px] sm:text-[56px] md:text-[72px] lg:text-[88px] font-black tracking-[-0.04em] leading-[0.92] text-ink">
                        Precision tooling
                        <br />
                        for{" "}
                        <span className="relative inline-block">
                            <span className="relative z-10">B2B</span>
                            <span className="absolute inset-x-0 bottom-[6px] h-[14px] bg-forge -z-0" />
                        </span>{" "}
                        prospecting.
                    </h1>

                    <p className="mt-8 max-w-xl text-lg md:text-xl text-ink-soft leading-relaxed">
                        Find real businesses by location and category. Discover
                        public contact emails. Send compliant outreach from your
                        own SMTP — without juggling spreadsheets and browser
                        tabs.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        <a
                            data-testid="hero-cta-demo"
                            href="/demo"
                            className="btn btn-accent"
                        >
                            Book a demo <ArrowUpRight size={14} />
                        </a>
                        <a
                            data-testid="hero-cta-pricing"
                            href="/pricing"
                            className="btn btn-secondary"
                        >
                            View pricing <ArrowUpRight size={14} />
                        </a>
                    </div>

                    <div className="mt-12 grid grid-cols-3 gap-px bg-border max-w-md">
                        {[
                            { k: "£29", v: "per month" },
                            { k: "£249", v: "per year" },
                            { k: version, v: "current build" },
                        ].map((s) => (
                            <div
                                key={s.k}
                                className="bg-bg p-4"
                                data-testid={`hero-stat-${s.k}`}
                            >
                                <AnimatedNumber
                                    value={s.k}
                                    className="font-display text-2xl font-bold text-ink block"
                                />
                                <div className="overline mt-1">{s.v}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right: screenshot panel */}
                <div className="lg:col-span-6 relative">
                    <div
                        className="absolute inset-0 -z-10 blueprint-bg opacity-60"
                        aria-hidden
                    />
                    <div className="relative border border-ink bg-bg shadow-[8px_8px_0_0_hsl(var(--ink))]">
                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-surface">
                            <div className="flex items-center gap-2">
                                <span className="h-2.5 w-2.5 bg-ink" />
                                <span className="h-2.5 w-2.5 bg-ink-mute" />
                                <span className="h-2.5 w-2.5 bg-ink-mute" />
                            </div>
                            <span className="font-mono text-[11px] text-ink-mute">
                                reachforge — lead workspace
                            </span>
                            <span className="font-mono text-[11px] text-ink-mute">
                                ● live
                            </span>
                        </div>
                        <img
                            data-testid="hero-product-image"
                            src="/assets/screenshots/reachforge-leads-email-discovery.svg"
                            alt="ReachForge email discovery interface"
                            className="block w-full h-auto"
                            loading="eager"
                        />
                    </div>
                    <div className="mt-4 flex items-center justify-between font-mono text-[11px] text-ink-mute">
                        <span>FIG.01 — Email discovery workspace</span>
                        <span>↳ /workspace/leads</span>
                    </div>
                </div>
            </div>

            {/* Step-marker strip — replaces the previous marquee. Teases the Workflow section. */}
            <div
                role="list"
                aria-label="Five-step ReachForge workflow: search, discover, score, send, track."
                data-testid="hero-step-strip"
                onMouseLeave={() => setActive(0)}
                className="border-t border-border bg-surface"
            >
                <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-5 md:py-7">
                    {/* Mobile: horizontal scroll. Desktop: flex with stretching connectors. */}
                    <div className="flex md:hidden gap-5 overflow-x-auto snap-x snap-mandatory -mx-6 px-6">
                        {steps.map((s, i) => (
                            <a
                                key={s.label}
                                role="listitem"
                                href="#workflow"
                                data-testid={`hero-step-${String(i + 1).padStart(2, "0")}`}
                                onMouseEnter={() => setActive(i)}
                                className="shrink-0 snap-start flex items-center gap-3 py-1"
                            >
                                <span
                                    className={`h-7 w-7 border border-ink flex items-center justify-center font-mono text-[11px] font-semibold transition-colors duration-150 ${
                                        active === i
                                            ? "bg-ink text-bg"
                                            : "bg-bg text-ink"
                                    }`}
                                >
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span className="font-display text-sm font-semibold text-ink whitespace-nowrap">
                                    {s.label}
                                </span>
                            </a>
                        ))}
                    </div>

                    <div className="hidden md:flex items-stretch justify-between gap-4">
                        {steps.map((s, i) => (
                            <React.Fragment key={s.label}>
                                <a
                                    role="listitem"
                                    href="#workflow"
                                    data-testid={`hero-step-${String(i + 1).padStart(2, "0")}`}
                                    onMouseEnter={() => setActive(i)}
                                    onFocus={() => setActive(i)}
                                    className="flex items-center gap-3 group/step"
                                >
                                    <span
                                        className={`h-8 w-8 border border-ink flex items-center justify-center font-mono text-[11px] font-semibold transition-colors duration-150 ${
                                            active === i
                                                ? "bg-ink text-bg"
                                                : "bg-bg text-ink"
                                        }`}
                                    >
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span>
                                        <span className="block font-display text-sm lg:text-base font-semibold text-ink leading-none">
                                            {s.label}
                                        </span>
                                        <span className="overline mt-1 block">
                                            {s.caption}
                                        </span>
                                    </span>
                                </a>
                                {i < steps.length - 1 && (
                                    <span
                                        aria-hidden
                                        className="flex-1 self-center relative h-px bg-border"
                                    >
                                        {active === i && (
                                            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-1.5 w-1.5 bg-forge" />
                                        )}
                                    </span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
